import { useState, useRef } from "react";
import { useTranslation } from "next-i18next";
import PropTypes from "prop-types";
import { useStateMachine } from "little-state-machine";
import { Selector } from "./Selector";
import { Footer } from "./Footer";
import { isBra } from "../lib/isBra";
import { getSize } from "../lib/product-utils";
import { SizeGuide } from "../";
import { resolveHasSizeData } from "../SizeGuide/lib/resolve-has-data";
import { SimpleBlockContent } from "../../SimpleBlockContent";
import styles from "./styles/form.module.css";
import { useForm } from "./hooks/useForm";

export function Form({
  section,
  sections,
  index,
  handleScrollTo,
  discountMessage,
  isStockup,
  slug,
}) {
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const sizeGuideRef = useRef(null);
  const [isFullDescription, setIsFullDescription] = useState(false);
  const { state } = useStateMachine();
  const {
    product,
    price,
    compareAtPrice,
    totalPrice,
    totalCompareAtPrice,
    navigation,
    options,
    handleOptionChange,
    handleNavigationChange,
    handleSubmit,
  } = useForm(sections, section, discountMessage, slug);
  const { t } = useTranslation("bundles");

  const toggleReadMore = () => setIsFullDescription(!isFullDescription);

  function closeSizeGuide() {
    setShowSizeGuide(false);
  }

  function openSizeGuide() {
    setShowSizeGuide(true);
  }

  const readMoreStyles = `
    ${isFullDescription ? styles.fullTextBlock : styles.textBlock}
  `;

  return (
    <>
      {state[`${section.key}-productGroup`].sizeGuide && (
        <SizeGuide
          variant={state[`${section.key}-variant`]}
          data={state[`${section.key}-productGroup`].sizeGuide}
          isBra={isBra(state[`${section.key}-productGroup`].slug)}
          showSizeGuide={showSizeGuide}
          closeSizeGuide={closeSizeGuide}
          selectedSize={getSize(state[`${section.key}-variant`])}
          sizes={options.get("Size")}
          handleOptionChange={handleOptionChange}
          showFooter={false}
          ref={sizeGuideRef}
        />
      )}
      <form onSubmit={handleSubmit} className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.info}>
            <header>
              <h2 className={styles.title}>
                {section.sectionTitle}
                <span className={styles.productTitle}>
                  {state[`${section.key}-productGroup`].title.replace(/(Wo)?[Mm]en['’]?s /g, "")}
                </span>
              </h2>
            </header>

            <section className={styles.priceContainer}>
              <span className={styles.price}>{price}</span>
              {compareAtPrice && (
                <span className={styles.compareAtPrice}>{compareAtPrice.replace(/[CA]/g, "")}</span>
              )}
            </section>

            {state[`${section.key}-productGroup`].description && (
              <section className={styles.description}>
                <div className={readMoreStyles}>
                  <SimpleBlockContent blocks={state[`${section.key}-productGroup`].description} />
                </div>
                {!isFullDescription && (
                  <button onClick={toggleReadMore} className={styles.readMore}>
                    {t("read-more-button")}
                  </button>
                )}
                {isFullDescription && (
                  <button onClick={toggleReadMore} className={styles.readMore}>
                    {t("read-less-button")}
                  </button>
                )}
              </section>
            )}
          </div>

          <div className={styles.selection}>
            {navigation && navigation.length > 1 && (
              <Selector
                data={navigation}
                heading={"Colour"}
                name={"colour"}
                updateVariant={handleNavigationChange}
                previewOnHover={handleNavigationChange}
              />
            )}

            {Array.from(options.keys()).map((key) => {
              if (options.get(key).length > 1) {
                return (
                  <Selector
                    key={key.toLowerCase()}
                    name={key.toLowerCase()}
                    data={Array.from(options.get(key).values())}
                    heading={key}
                    updateVariant={handleOptionChange}
                    openSizeGuide={
                      resolveHasSizeData(state[`${section.key}-productGroup`].sizeGuide)
                        ? openSizeGuide
                        : null
                    }
                  />
                );
              }
            })}
          </div>
        </div>
        <Footer
          index={index}
          sections={sections}
          handleScrollTo={handleScrollTo}
          totalPrice={totalPrice}
          totalCompareAtPrice={totalCompareAtPrice}
          isStockup={isStockup}
          discountMessage={discountMessage}
          handleSubmit={handleSubmit}
        />
      </form>
    </>
  );
}

Form.propTypes = {
  section: PropTypes.object.isRequired,
  sections: PropTypes.array.isRequired,
  index: PropTypes.number.isRequired,
  handleScrollTo: PropTypes.func.isRequired,
  discountMessage: PropTypes.string,
  isStockup: PropTypes.bool.isRequired,
};
